import React, { useState, useMemo } from 'react';
import { tkr, getISOWeek, SOMMAR_VECKOR, vinterVeckor } from '../utils';
import { TrendCompare } from '../components/SharedComponents';
import { mergeSeasons } from './paceMerge';

const ACCENT = '#1d6a5f';
const parseISO = (s) => new Date(s + 'T12:00:00Z');
const iso = (d) => d.toISOString().slice(0, 10);
function isoMonday(y, w) {
  const jan4 = new Date(Date.UTC(y, 0, 4));
  const dow = (jan4.getUTCDay() + 6) % 7;
  const wk1 = new Date(jan4); wk1.setUTCDate(jan4.getUTCDate() - dow);
  const d = new Date(wk1); d.setUTCDate(wk1.getUTCDate() + (w - 1) * 7);
  return d;
}
function belCell(b) {
  if (!b || b <= 0) return ['transparent', 'var(--text-secondary)'];
  if (b < 0.2) return ['#E1F5EE', '#0F6E56'];
  if (b < 0.4) return ['#9FE1CB', '#04342C'];
  if (b < 0.6) return ['#5DCAA5', '#04342C'];
  if (b < 0.8) return ['#1D9E75', '#ffffff'];
  return ['#0F6E56', '#ffffff'];
}
function histAt(PH, seasonId, asOf) {
  const m = {}; const ps = PH[seasonId]; if (!ps) return m;
  for (const r of ps.rows) if (r.asOf === asOf && r.bel != null) m[r.week] = { bel: r.bel / 100, gn: r.gastn, rumsn: r.rumsn, varde: r.varde };
  return m;
}
// fjolårets slututfall per vecka (sista avstämningen)
function priorFinal(PH, seasonId) {
  const ps = PH[seasonId]; const m = {}; if (!ps) return m;
  for (const r of ps.rows) { if (r.bel == null) continue; const c = m[r.week]; if (!c || r.asOf > c.asOf) m[r.week] = { asOf: r.asOf, bel: r.bel / 100, gn: r.gastn, rumsn: r.rumsn, varde: r.varde }; }
  return m;
}
// fjolårets avstämning vars ISO-vecka ligger närmast N (±4)
function priorAtAvst(PH, seasonId, N) {
  const ps = PH[seasonId]; if (!ps) return { map: {}, week: null };
  let best = null, bd = 99;
  for (const s of ps.snapshots || []) { const w = getISOWeek(s); let d = Math.abs(w - N); d = Math.min(d, 53 - d); if (d < bd) { bd = d; best = s; } }
  return (best && bd <= 4) ? { map: histAt(PH, seasonId, best), week: getISOWeek(best) } : { map: {}, week: null };
}

export default function BokningslageTable({ byDatum, totalRooms = 45, paceLive }) {
  const PH = useMemo(() => mergeSeasons(paceLive), [paceLive]);
  const now = new Date();
  const y = now.getUTCFullYear(), mo = now.getUTCMonth();
  const sommarY = mo >= 10 ? y + 1 : y;
  const vinterY = mo >= 4 ? y : y - 1;
  const yy = (n) => String(n).slice(2);
  const summerWeeks = SOMMAR_VECKOR;
  const mkSummer = (yr) => ({ id: 'sommar' + yr, label: `Sommar ${yr}`, typ: 'sommar', weeks: summerWeeks, yr, hist: `sommar-${yr}`, prior: `sommar-${yr - 1}` });
  const mkWinter = (sy) => ({ id: 'vinter' + sy, label: `Vinter ${yy(sy)}/${yy(sy + 1)}`, typ: 'vinter', weeks: vinterVeckor(sy), y1: sy, y2: sy + 1, hist: `vinter-${yy(sy)}${yy(sy + 1)}`, prior: `vinter-${yy(sy - 1)}${yy(sy)}` });
  // innevarande + kommande säsonger (rullar automatiskt)
  const SEASONS = [mkSummer(sommarY), mkWinter(vinterY), mkSummer(sommarY + 1), mkWinter(vinterY + 1)];

  const [sid, setSid] = useState((mo >= 4 && mo <= 9 ? mkSummer(sommarY) : mkWinter(vinterY)).id);
  const S = SEASONS.find(s => s.id === sid) || SEASONS[0];
  const weekYear = (w) => S.typ === 'sommar' ? S.yr : (w >= 40 ? S.y1 : S.y2);

  const asOfOpts = useMemo(() => ['live', ...((PH[S.hist] && PH[S.hist].snapshots) || []).slice().reverse()], [sid, PH]); // eslint-disable-line
  const [asOf, setAsOf] = useState('live');
  const curAsOf = asOfOpts.includes(asOf) ? asOf : 'live';
  const avstWeek = curAsOf === 'live' ? getISOWeek(now) : getISOWeek(curAsOf);
  const liveMode = curAsOf === 'live';

  const [jmf, setJmf] = useState('ledtid'); // 'ledtid' | 'utfall'

  // "i år"-rader vid vald as-of (live = inkl. offert ur Qlik; historisk = ur arken)
  const iAr = useMemo(() => {
    if (!liveMode) return histAt(PH, S.hist, curAsOf);
    const m = {};
    for (const w of S.weeks) {
      // Värde = logi + förbokad restaurang, samma definition som arken ifjol.
      const mon = isoMonday(weekYear(w), w); let rumsn = 0, gn = 0, varde = 0;
      for (let i = 0; i < 7; i++) { const d = new Date(mon); d.setUTCDate(mon.getUTCDate() + i); const rec = byDatum[iso(d)]; if (rec) { rumsn += rec.rumRumO; gn += rec.rumGO; varde += rec.logi + (rec.mat || 0); } }
      m[w] = { bel: rumsn / (totalRooms * 7), gn, rumsn, varde };
    }
    return m;
  }, [sid, curAsOf, byDatum, PH]); // eslint-disable-line

  const fjolRes = useMemo(() => jmf === 'utfall' ? { map: priorFinal(PH, S.prior), week: null } : priorAtAvst(PH, S.prior, avstWeek), [sid, avstWeek, jmf, PH]); // eslint-disable-line
  const fjol = fjolRes.map;
  const fjLabel = jmf === 'utfall' ? 'Ifjol utfall' : `Ifjol (v.${fjolRes.week ?? avstWeek})`;

  // Förra avstämningen (≥6 dgr tidigare) → pickup "jmf frg vecka" (på rumsnätter)
  const curRefDate = liveMode ? iso(now) : curAsOf;
  const prevAsOf = useMemo(() => {
    let best = null;
    for (const s of (PH[S.hist] && PH[S.hist].snapshots) || []) {
      if (s >= curRefDate) continue;
      if ((parseISO(curRefDate) - parseISO(s)) / 86400000 >= 6 && (!best || s > best)) best = s;
    }
    return best;
  }, [sid, curAsOf, PH]); // eslint-disable-line
  const prevMap = useMemo(() => prevAsOf ? histAt(PH, S.hist, prevAsOf) : {}, [prevAsOf, PH]); // eslint-disable-line

  const rows = S.weeks.map(w => {
    const a = iAr[w] || { bel: 0, gn: 0, rumsn: 0, varde: 0 };
    const f = fjol[w] || null; const pv = prevMap[w] || null;
    return { w, mon: isoMonday(weekYear(w), w), ...a, fjBel: f ? f.bel : null, fjGn: f ? f.gn : null, fjVarde: f ? f.varde : null,
      pvRumsn: pv ? pv.rumsn : null, frgPct: (pv && pv.rumsn) ? Math.round((a.rumsn - pv.rumsn) / pv.rumsn * 100) : null };
  });
  const sum = (k) => rows.reduce((s, r) => s + (r[k] || 0), 0);
  const fjBelVals = rows.map(r => r.fjBel).filter(v => v != null);
  const tot = {
    rumsn: sum('rumsn'), gn: sum('gn'), varde: sum('varde'),
    fjGn: rows.reduce((s, r) => s + (r.fjGn || 0), 0), fjVarde: rows.reduce((s, r) => s + (r.fjVarde || 0), 0),
    bel: sum('rumsn') / (totalRooms * 7 * rows.length),
    fjBel: fjBelVals.length ? fjBelVals.reduce((a, b) => a + b, 0) / fjBelVals.length : null,
    pvRumsn: rows.reduce((s, r) => s + (r.pvRumsn || 0), 0),
  };
  const pct = (cur, fj) => fj ? Math.round((cur - fj) / fj * 100) : null;
  const gnPct = pct(tot.gn, tot.fjGn), vdPct = pct(tot.varde, tot.fjVarde);
  const totFrgPct = prevAsOf && tot.pvRumsn ? Math.round((tot.rumsn - tot.pvRumsn) / tot.pvRumsn * 100) : null;
  const frgCell = (v) => v == null ? <span style={{ color: 'var(--text-secondary)' }}>–</span> : <span style={{ color: v > 0 ? '#1D9E75' : v < 0 ? '#A32D2D' : 'var(--text-secondary)', fontWeight: 600 }}>{v >= 0 ? '+' : ''}{v}%</span>;

  const chip = (active, label, onClick) => (
    <button onClick={onClick} style={{ fontSize: 12.5, fontWeight: 600, padding: '6px 13px', borderRadius: 999, border: '1px solid ' + (active ? ACCENT : 'var(--border)'), background: active ? ACCENT : 'transparent', color: active ? '#fff' : 'var(--text-secondary)', cursor: 'pointer', whiteSpace: 'nowrap' }}>{label}</button>
  );
  const th = { fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.02em', padding: '8px 8px', textAlign: 'right', borderBottom: '1px solid var(--border)' };
  const td = { fontSize: 13, padding: '7px 8px', textAlign: 'right', borderBottom: '1px solid var(--border)' };
  const fjStyle = { ...td, color: 'var(--text-secondary)' };
  const dStr = (d) => `${d.getUTCDate()}/${d.getUTCMonth() + 1}`;
  const ppCell = (cur, fj) => { if (fj == null) return <span style={{ color: 'var(--text-secondary)' }}>–</span>; const d = Math.round((cur - fj) * 100); return <span style={{ color: d >= 0 ? '#1D9E75' : '#A32D2D', fontWeight: 600 }}>{d >= 0 ? '+' : ''}{d} pp</span>; };
  const kpiCard = (label, value, sub, color) => (
    <div style={{ background: 'var(--surface-2,#faf9f6)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || ACCENT, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ fontFamily: 'system-ui,sans-serif' }}>
      <div className="card">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>Säsong</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{SEASONS.map(s => chip(sid === s.id, s.label, () => { setSid(s.id); setAsOf('live'); }))}</div>
          </div>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>Avstämning (tidpunkt)</div>
            <select value={curAsOf} onChange={e => setAsOf(e.target.value)} style={{ fontSize: 13, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)' }}>
              {asOfOpts.map(o => <option key={o} value={o}>{o === 'live' ? `Nu · live (v.${getISOWeek(now)})` : `v.${getISOWeek(o)} · ${dStr(parseISO(o))}`}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>Jämför mot fjol</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {chip(jmf === 'ledtid', 'Samma ledtid', () => setJmf('ledtid'))}
              {chip(jmf === 'utfall', 'Slututfall', () => setJmf('utfall'))}
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-title">
          Bokningsläge {S.label} — avstämning v.{avstWeek} {liveMode ? '(live)' : ''} · {jmf === 'utfall' ? 'jmf mot ifjols slututfall' : `jmf mot ifjols avstämning v.${fjolRes.week ?? avstWeek}`}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={{ ...th, textAlign: 'left' }}>Vecka</th>
              <th style={th}>Beläggning</th>
              <th style={th}>{fjLabel}</th>
              <th style={th}>Δ ifjol</th>
              <th style={th}>Rumsn.</th>
              <th style={th} title="Förändring i rumsnätter sedan förra avstämningen">Δ frg v.</th>
              <th style={th}>Gästn.</th>
              <th style={th}>Ifjol gn</th>
              <th style={th}>Värde</th>
              <th style={th}>Ifjol värde</th>
            </tr></thead>
            <tbody>
              {rows.map(r => {
                const [bg, fg] = belCell(r.bel);
                return (
                  <tr key={r.w}>
                    <td style={{ ...td, textAlign: 'left' }}><strong>v.{r.w}</strong> <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>{dStr(r.mon)}</span></td>
                    <td style={{ ...td, background: bg, color: fg, fontWeight: 600 }}>{Math.round(r.bel * 100)}%</td>
                    <td style={fjStyle}>{r.fjBel != null ? Math.round(r.fjBel * 100) + '%' : '–'}</td>
                    <td style={td}>{ppCell(r.bel, r.fjBel)}</td>
                    <td style={td}>{r.rumsn || '–'}</td>
                    <td style={td}>{frgCell(r.frgPct)}</td>
                    <td style={td}>{r.gn || '–'}</td>
                    <td style={fjStyle}>{r.fjGn != null ? r.fjGn : '–'}</td>
                    <td style={td}>{r.varde > 0 ? tkr(r.varde) : '–'}</td>
                    <td style={fjStyle}>{r.fjVarde != null ? tkr(r.fjVarde) : '–'}</td>
                  </tr>
                );
              })}
              <tr style={{ borderTop: '2px solid var(--border)', fontWeight: 700 }}>
                <td style={{ ...td, textAlign: 'left', fontWeight: 700 }}>Totalt</td>
                <td style={{ ...td, fontWeight: 700 }}>{Math.round(tot.bel * 100)}%</td>
                <td style={{ ...fjStyle }}>{tot.fjBel != null ? Math.round(tot.fjBel * 100) + '%' : '–'}</td>
                <td style={td}>{tot.fjBel != null ? ppCell(tot.bel, tot.fjBel) : '–'}</td>
                <td style={{ ...td, fontWeight: 700 }}>{tot.rumsn}</td>
                <td style={td}>{frgCell(totFrgPct)}</td>
                <td style={{ ...td, fontWeight: 700 }}>{tot.gn}</td>
                <td style={fjStyle}>{tot.fjGn || '–'}</td>
                <td style={{ ...td, fontWeight: 700 }}>{tkr(tot.varde)}</td>
                <td style={fjStyle}>{tot.fjVarde ? tkr(tot.fjVarde) : '–'}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="note" style={{ marginTop: 8 }}>
          {liveMode ? 'Live från Visbook, inkl. offert (på böckerna nu). Värde = logi + förbokad restaurang, samma definition som arken — jämförbart med ifjol.' : 'Historisk avstämning ur era ark (inkl. offert). Värde = logi + förbokad restaurang.'}
          {prevAsOf ? ` Δ frg v. = förändring i rumsnätter sedan förra avstämningen (v.${getISOWeek(prevAsOf)} · ${dStr(parseISO(prevAsOf))}).` : ''}
        </div>
      </div>

      {/* Linjediagram: beläggning per vecka — i år vs ifjol (följer tabellens val) */}
      <div className="card">
        <div style={{ display: 'flex', gap: 16, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', marginBottom: 2 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>
            Beläggning per vecka — i år vs {jmf === 'utfall' ? 'ifjols slututfall' : 'ifjol (samma ledtid)'}
          </div>
          <div style={{ display: 'flex', gap: 16, fontSize: 11, color: 'var(--text-secondary)' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 11, height: 3, borderRadius: 2, background: ACCENT }} />I år</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 11, height: 3, borderRadius: 2, background: '#C2A878' }} />{fjLabel}</span>
          </div>
        </div>
        <TrendCompare
          data={rows.map(r => ({ label: `v${r.w}`, value: Math.round(r.bel * 100), prev: r.fjBel != null ? Math.round(r.fjBel * 100) : null }))}
          formatVal={(v) => `${Math.round(v)}%`}
          curColor={ACCENT} prevColor="#C2A878"
        />
        <div className="note">Samma data som tabellen ovan — styrs av säsong, avstämning och jämförelseval. Hovra för exakta värden.</div>
      </div>

      {/* Period-YoY för hela säsongen */}
      <div className="card">
        <div className="card-title">Säsongen v.{S.weeks[0]}–{S.weeks[S.weeks.length - 1]}: i år vs i fjol ({jmf === 'utfall' ? 'slututfall' : `avstämning v.${fjolRes.week ?? avstWeek}`})</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
          {kpiCard('Gästnätter', tot.gn.toLocaleString('sv-SE'), `i fjol ${tot.fjGn.toLocaleString('sv-SE')}${gnPct != null ? `  ·  ${gnPct >= 0 ? '+' : ''}${gnPct}%` : ''}`, gnPct != null ? (gnPct >= 0 ? '#1D9E75' : '#A32D2D') : ACCENT)}
          {kpiCard('Bokningsvärde', tkr(tot.varde), `i fjol ${tkr(tot.fjVarde)}${vdPct != null ? `  ·  ${vdPct >= 0 ? '+' : ''}${vdPct}%` : ''}`, vdPct != null ? (vdPct >= 0 ? '#1D9E75' : '#A32D2D') : ACCENT)}
          {kpiCard('Beläggning snitt', Math.round(tot.bel * 100) + '%', tot.fjBel != null ? `i fjol ${Math.round(tot.fjBel * 100)}%` : '')}
        </div>
      </div>
    </div>
  );
}
